import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TabBar from '../components/TabBar';
import { useToast } from '../components/Toast';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatBJTime } from '../utils/datetime';
import { StatusBadge } from '../components/PuzzleImageViewer';
import { ArrowLeft, Puzzle, Inbox } from 'lucide-react';

// 我的 → 证券识别记录：版式和“排五识别记录”一致，只看识别结果。
//   按日期分组，每条一张卡片：号码 + 状态（已计入 / 待核实 / 已作废 / 已锁定 / 草稿）、所属包与第几单、时间。
//   看图片、补全识别失败的信息都在工作台底部的“图片管理”里，这里不放入口（也不放跳转链接）。前台没有导出功能。
// 本人只看自己；代理商看名下全部；管理员看全量（范围由服务端控制）。
const FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'counted', label: '已计入', countKey: 'counted' },
  { key: 'pending', label: '待核实', countKey: 'pending' },
  { key: 'rejected', label: '已作废', countKey: 'rejected' },
];
const PAGE_SIZE = 30;

export default function PuzzleRecordsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ counted: 0, pending: 0, rejected: 0, locked: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const multiUser = user?.role !== 'user';

  const load = useCallback(async (pg, f, append) => {
    setLoading(true);
    try {
      const res = await api.get('/puzzle/records', { params: { status: f, page: pg, pageSize: PAGE_SIZE } });
      setList(prev => (append ? [...prev, ...res.data.list] : res.data.list));
      setTotal(res.data.total);
      setSummary(res.data.summary || {});
      setPage(pg);
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(1, filter, false); }, [load, filter]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => { if (event === 'puzzle_updated' || event === 'puzzle_session_updated') load(1, filter, false); });
  }, [subscribe, load, filter]);

  // 按提交日期分组（草稿等没有提交时间的按创建日期）
  const dayOf = (r) => String(r.confirmed_at || r.created_at || '').slice(0, 10);
  const dates = [];
  const byDate = {};
  list.forEach(r => {
    const d = dayOf(r);
    if (!byDate[d]) { byDate[d] = []; dates.push(d); }
    byDate[d].push(r);
  });

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate('/me')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><Puzzle size={16} /> 证券识别记录</span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 状态筛选：数字是各状态的总条数 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 4, overflowX: 'auto' }}>
          {FILTERS.map(f => {
            const active = filter === f.key;
            const n = f.countKey ? (summary[f.countKey] || 0) : null;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, flexShrink: 0, cursor: 'pointer',
                border: `1.5px solid ${active ? 'var(--c-primary)' : 'var(--c-border)'}`,
                background: active ? 'var(--c-primary)' : 'var(--c-surface)',
                color: active ? '#fff' : 'var(--c-text-secondary)',
              }}>{f.label}{n !== null ? ` ${n}` : ''}</button>
            );
          })}
        </div>

        {loading && list.length === 0 ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : list.length === 0 ? (
          <div className="card empty-state" style={{ padding: '40px 20px', marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Inbox size={44} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p style={{ fontWeight: 600 }}>暂无记录</p>
          </div>
        ) : dates.map(date => (
          <div key={date}>
            {/* 日期分隔（和排五识别记录一样） */}
            <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '12px 2px 6px' }}>{date.slice(5)}</div>
            {byDate[date].map(r => (
              <div key={r.id} style={{
                border: '1px solid var(--c-border)', borderRadius: 14, overflow: 'hidden', marginBottom: 10, background: 'var(--c-surface)',
                padding: '12px 14px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>{r.phone || '—'}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.package_name}{r.seq_no != null ? ` · 第 ${r.seq_no} 单` : ''}{r.nickname ? ` · ${r.nickname}` : ''}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 3 }}>
                  {formatBJTime(r.confirmed_at || r.created_at)}
                  {multiUser && r.user_name ? ` · ${r.user_name}` : ''}
                  {r.is_settled ? ' · 已结算' : ''}
                </div>
              </div>
            ))}
          </div>
        ))}

        {!loading && list.length < total && (
          <button className="btn btn-outline" onClick={() => load(page + 1, filter, true)} style={{ height: 42 }}>加载更多（{list.length} / {total}）</button>
        )}
        {loading && list.length > 0 && <p style={{ textAlign: 'center', padding: 16, color: 'var(--c-text-secondary)' }}>加载中...</p>}
      </div>

      <TabBar mode="user" />
    </div>
  );
}
